import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Calendar,
  BookOpen,
  ArrowRight,
  Clock,
  AlertTriangle,
  FileText,
  CheckCircle2,
  FolderOpen,
  PenLine,
  ClipboardList,
  Target
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { format, isToday, isTomorrow, differenceInDays } from 'date-fns';
import { toast } from 'sonner';
import GoogleDocsPicker from '../components/GoogleDocsPicker';

const projectTypeColors = {
  EE: 'bg-red-50 text-red-700 border-red-200',
  IA: 'bg-blue-50 text-blue-700 border-blue-200',
  TOK_ESSAY: 'bg-amber-50 text-amber-700 border-amber-200',
  TOK_EXHIBITION: 'bg-amber-50 text-amber-700 border-amber-200',
};

const deadlineTypeLabel = {
  OFFICIAL: 'OFFICIAL',
  MOCK: 'MOCK',
  PERSONAL: 'PERSONAL',
};

const DashboardPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [essays, setEssays] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [overdueDeadlines, setOverdueDeadlines] = useState([]);
  const [recentSources, setRecentSources] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [satSummary, setSatSummary] = useState(null);
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const [selectedProjectForDoc, setSelectedProjectForDoc] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, essaysRes, upcomingRes, overdueRes, sourcesRes, sessionsRes, satRes] = await Promise.all([
        api.get('/projects'),
        api.get('/essays'),
        api.get('/deadlines/upcoming'),
        api.get('/deadlines/overdue'),
        api.get('/sources'),
        api.get('/work-sessions/upcoming'),
        api.get('/sat/summary').catch(() => ({ data: { hasPlan: false } })),
      ]);
      setProjects(projectsRes.data.projects || []);
      setEssays(essaysRes.data.essays || []);
      setUpcomingDeadlines(upcomingRes.data.deadlines || []);
      setOverdueDeadlines(overdueRes.data.deadlines || []);
      setRecentSources((sourcesRes.data.sources || []).slice(0, 5));
      setUpcomingSessions(sessionsRes.data.sessions || []);
      setSatSummary(satRes.data || null);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDeadlineDate = (date) => {
    const d = new Date(date);
    if (isToday(d)) return 'Today';
    if (isTomorrow(d)) return 'Tomorrow';
    const days = differenceInDays(d, new Date());
    if (days < 7) return `${days}d`;
    return format(d, 'MMM d');
  };

  const handleAttachDocClick = () => {
    if (projects.length === 0) {
      toast.info('Create a project first to attach documents');
      navigate('/projects');
      return;
    }
    setShowProjectPicker(true);
  };

  const handleProjectSelected = (project) => {
    setSelectedProjectForDoc(project);
  };

  const handleDocSelected = async (docData) => {
    try {
      await api.post(`/project-docs/${selectedProjectForDoc.id}`, {
        url: docData.url,
        title: docData.title,
        role: docData.role || 'DRAFT',
        isCurrent: docData.isCurrent || false
      });
      toast.success(`Document attached to ${selectedProjectForDoc.title || selectedProjectForDoc.type}`);
      setShowProjectPicker(false);
      setSelectedProjectForDoc(null);
      navigate(`/projects/${selectedProjectForDoc.id}`);
    } catch (err) {
      toast.error('Failed to attach document');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm text-muted-foreground font-mono animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto animate-fade-in" data-testid="dashboard-page">
      {/* Page Header */}
      <div className="border-b border-border pb-5 mb-6">
        <p className="label-swiss mb-1">Overview</p>
        <h1 className="text-2xl font-bold text-foreground tracking-tight">
          {user?.name?.split(' ')[0]}
        </h1>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 border border-border mb-6">
        <StatCell label="IB Projects" value={projects.length} />
        <StatCell label="Essays" value={essays.length} className="border-l border-border" />
        <StatCell label="Upcoming" value={upcomingDeadlines.length} className="border-l border-border" />
        <StatCell label="Sources" value={recentSources.length} className="border-l border-border" />
      </div>

      {/* Quick Actions */}
      <div className="border border-border mb-6">
        <div className="px-4 py-2.5 border-b border-border">
          <p className="label-swiss">Quick Actions</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-border">
          {[
            { icon: Plus, label: 'New Project', action: () => navigate('/projects'), testId: 'quick-action-new-project' },
            { icon: PenLine, label: 'New Essay', action: () => navigate('/essays'), testId: 'quick-action-new-essay' },
            { icon: Calendar, label: 'Add Deadline', action: () => navigate('/deadlines'), testId: 'quick-action-add-deadline' },
            { icon: BookOpen, label: 'Add Source', action: () => navigate('/research'), testId: 'quick-action-add-source' },
            { icon: FolderOpen, label: 'Attach Doc', action: handleAttachDocClick, testId: 'quick-action-attach-doc' },
          ].map((a) => (
            <button
              key={a.label}
              onClick={a.action}
              data-testid={a.testId}
              className="flex flex-col items-center gap-1.5 py-4 px-3 hover:bg-muted/60 transition-colors group col-span-1"
            >
              <a.icon className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
              <span className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground group-hover:text-foreground">
                {a.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        {/* Overdue Alert */}
        {overdueDeadlines.length > 0 && (
          <div className="md:col-span-12 border border-destructive/40 bg-destructive/5 px-4 py-3 flex items-center gap-3">
            <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-xs font-semibold text-destructive uppercase tracking-wide mr-3">
                Overdue ({overdueDeadlines.length})
              </span>
              <span className="text-xs text-muted-foreground">
                {overdueDeadlines.slice(0, 3).map(d => d.label).join(' · ')}
                {overdueDeadlines.length > 3 && ` · +${overdueDeadlines.length - 3} more`}
              </span>
            </div>
            <Link to="/deadlines" className="text-xs text-destructive font-medium shrink-0">
              View →
            </Link>
          </div>
        )}

        {/* Upcoming Deadlines */}
        <div className="md:col-span-8 border border-border">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-muted-foreground" />
              <p className="label-swiss">Upcoming Deadlines</p>
            </div>
            <Link to="/deadlines" className="text-[11px] text-primary font-medium flex items-center gap-1 uppercase tracking-wide">
              All <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {upcomingDeadlines.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm text-muted-foreground mb-2">No upcoming deadlines</p>
              <button onClick={() => navigate('/deadlines')} className="text-xs text-primary font-medium uppercase tracking-wide">
                Add a deadline →
              </button>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {upcomingDeadlines.slice(0, 5).map((deadline) => (
                <div key={deadline.id} className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="label-swiss shrink-0">{deadlineTypeLabel[deadline.type] || deadline.type}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{deadline.label}</p>
                      {deadline.project && (
                        <p className="text-[11px] text-muted-foreground">{deadline.project.title || deadline.project.type}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-4">
                    <p className="text-sm font-semibold text-foreground font-mono">{formatDeadlineDate(deadline.dueAt)}</p>
                    {!deadline.allDay && (
                      <p className="text-[11px] text-muted-foreground font-mono">{format(new Date(deadline.dueAt), 'HH:mm')}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Projects */}
        <div className="md:col-span-4 border border-border">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <FileText className="w-3.5 h-3.5 text-muted-foreground" />
              <p className="label-swiss">IB Projects</p>
            </div>
            <Link to="/projects" className="text-[11px] text-primary font-medium flex items-center gap-1 uppercase tracking-wide">
              All <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {projects.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm text-muted-foreground mb-2">No projects yet</p>
              <button onClick={() => navigate('/projects')} className="text-xs text-primary font-medium uppercase tracking-wide">
                Create first →
              </button>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {projects.slice(0, 4).map((project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors group"
                >
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 border ${projectTypeColors[project.type]} uppercase tracking-wide shrink-0`}>
                    {project.type.replace('_', ' ')}
                  </span>
                  <p className="text-sm text-foreground truncate flex-1 group-hover:text-primary transition-colors">
                    {project.title || project.subject || 'Untitled'}
                  </p>
                  <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Essays */}
        <div className="md:col-span-4 border border-border">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <PenLine className="w-3.5 h-3.5 text-muted-foreground" />
              <p className="label-swiss">College Essays</p>
            </div>
            <Link to="/college/essays" className="text-[11px] text-primary font-medium flex items-center gap-1 uppercase tracking-wide">
              All <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {essays.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm text-muted-foreground mb-2">No essays yet</p>
              <button onClick={() => navigate('/essays')} className="text-xs text-primary font-medium uppercase tracking-wide">
                Add first essay →
              </button>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {essays.slice(0, 4).map((essay) => {
                const essayTitle = essay.title || essay.school ||
                  { COMMON_APP_PS: 'CommonApp PS', COMMON_APP_AI: 'Additional Info', SUPPLEMENTAL: 'Supplemental', UC_PIQ: 'UC PIQ', COALITION: 'Coalition', SCHOLARSHIP: 'Scholarship', OTHER: 'Other' }[essay.type];
                const completed = essay.checklistItems?.filter(i => i.completed).length || 0;
                const total = essay.checklistItems?.length || 0;
                return (
                  <Link
                    key={essay.id}
                    to={`/college/essays/${essay.id}`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors group"
                  >
                    <span className="text-[10px] font-bold px-1.5 py-0.5 border bg-purple-50 text-purple-700 border-purple-200 uppercase tracking-wide shrink-0">
                      {essay.type === 'COMMON_APP_PS' ? 'CA PS' : essay.type === 'SUPPLEMENTAL' ? 'SUPP' : essay.type === 'UC_PIQ' ? 'UC' : essay.type.slice(0, 4)}
                    </span>
                    <p className="text-sm text-foreground truncate flex-1 group-hover:text-primary transition-colors">
                      {essayTitle}
                    </p>
                    {total > 0 && (
                      <span className="text-[10px] font-mono text-muted-foreground shrink-0">{completed}/{total}</span>
                    )}
                    <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* SAT Prep */}
        <div className="md:col-span-4 border border-border">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <Target className="w-3.5 h-3.5 text-muted-foreground" />
              <p className="label-swiss">SAT Prep</p>
            </div>
            <Link to="/sat-prep" className="text-[11px] text-primary font-medium flex items-center gap-1 uppercase tracking-wide">
              All <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {(!satSummary || (!satSummary.targetTotal && satSummary.latestTotal == null)) ? (
            <div className="py-10 text-center">
              <p className="text-sm text-muted-foreground mb-2">No SAT goal set</p>
              <button onClick={() => navigate('/sat-prep')} className="text-xs text-primary font-medium uppercase tracking-wide">
                Set your goal →
              </button>
            </div>
          ) : (
            <div className="px-4 py-4">
              <div className="flex items-end justify-between mb-3">
                <div>
                  <p className="text-3xl font-bold font-mono tracking-tight text-foreground">{satSummary.latestTotal ?? '—'}</p>
                  <p className="label-swiss mt-0.5">Latest{satSummary.targetTotal ? ` / ${satSummary.targetTotal}` : ''}</p>
                </div>
                {satSummary.daysUntilTest != null && satSummary.daysUntilTest >= 0 && (
                  <div className="text-right">
                    <p className="text-xl font-bold font-mono text-foreground">{satSummary.daysUntilTest}</p>
                    <p className="label-swiss mt-0.5">days left</p>
                  </div>
                )}
              </div>
              {satSummary.targetTotal && satSummary.latestTotal != null && (
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${Math.max(0, Math.min(100, (satSummary.latestTotal / satSummary.targetTotal) * 100))}%` }} />
                </div>
              )}
              {satSummary.deltaToTarget != null && (
                <p className="text-[11px] mt-2 text-muted-foreground">
                  {satSummary.deltaToTarget >= 0
                    ? `${satSummary.deltaToTarget} above target`
                    : `${Math.abs(satSummary.deltaToTarget)} points to go`}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Recent Sources */}
        <div className="md:col-span-6 border border-border">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <BookOpen className="w-3.5 h-3.5 text-muted-foreground" />
              <p className="label-swiss">Recent Sources</p>
            </div>
            <Link to="/research" className="text-[11px] text-primary font-medium flex items-center gap-1 uppercase tracking-wide">
              Library <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {recentSources.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm text-muted-foreground mb-2">No sources saved yet</p>
              <button onClick={() => navigate('/research')} className="text-xs text-primary font-medium uppercase tracking-wide">
                Add first source →
              </button>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {recentSources.map((source) => (
                <Link
                  key={source.id}
                  to={`/research/${source.id}`}
                  className="block px-4 py-3 hover:bg-muted/30 transition-colors"
                >
                  <p className="text-sm font-medium text-foreground line-clamp-1">{source.title}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {source.authors?.length > 0 ? source.authors[0] : 'Unknown author'}
                    {source.publishedDate && <span className="font-mono ml-2">{source.publishedDate.split('-')[0]}</span>}
                  </p>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Study Planner */}
        <div className="md:col-span-6 border border-border">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-3.5 h-3.5 text-muted-foreground" />
              <p className="label-swiss">Upcoming Study Sessions</p>
            </div>
            <Link to="/planner" className="text-[11px] text-primary font-medium flex items-center gap-1 uppercase tracking-wide">
              Planner <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {upcomingSessions.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm text-muted-foreground mb-2">No study sessions planned</p>
              <button onClick={() => navigate('/planner')} className="text-xs text-primary font-medium uppercase tracking-wide">
                Plan a session →
              </button>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {upcomingSessions.slice(0, 4).map((session) => (
                <Link
                  key={session.id}
                  to="/planner"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors group"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
                      {session.title}
                    </p>
                    <p className="text-[11px] text-muted-foreground font-mono">
                      {format(new Date(session.scheduledAt), 'EEE MMM d · HH:mm')}
                      {' · '}
                      {session.durationMinutes < 60
                        ? `${session.durationMinutes}m`
                        : `${Math.floor(session.durationMinutes / 60)}h${session.durationMinutes % 60 ? ` ${session.durationMinutes % 60}m` : ''}`}
                    </p>
                    {session.goal && (
                      <p className="text-[10px] text-muted-foreground mt-0.5 truncate flex items-center gap-1">
                        <Target className="w-2.5 h-2.5 shrink-0" /> {session.goal}
                      </p>
                    )}
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Progress Summary */}
        <div className="md:col-span-6 border border-border">
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-muted-foreground" />
              <p className="label-swiss">Progress Summary</p>
            </div>
          </div>
          <div className="grid grid-cols-3 divide-x divide-border">
            <SummaryCell label="Projects" value={projects.length} />
            <SummaryCell label="Deadlines" value={upcomingDeadlines.length + overdueDeadlines.length} />
            <SummaryCell label="Sources" value={recentSources.length} />
          </div>
          {projects.length > 0 && (
            <div className="border-t border-border px-4 py-3">
              <p className="label-swiss mb-2">By Type</p>
              <div className="space-y-2">
                {['EE', 'IA', 'TOK_ESSAY', 'TOK_EXHIBITION'].map((type) => {
                  const count = projects.filter(p => p.type === type).length;
                  if (count === 0) return null;
                  const pct = Math.round((count / projects.length) * 100);
                  return (
                    <div key={type} className="flex items-center gap-3">
                      <span className="text-[10px] font-mono text-muted-foreground w-20 shrink-0">
                        {type.replace('_', ' ')}
                      </span>
                      <div className="flex-1 h-1 bg-muted">
                        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[10px] font-mono text-muted-foreground w-6 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Google Docs Picker Modal */}
      <Dialog open={showProjectPicker} onOpenChange={(open) => {
        setShowProjectPicker(open);
        if (!open) setSelectedProjectForDoc(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedProjectForDoc ? 'Attach Google Doc' : 'Select a Project'}
            </DialogTitle>
          </DialogHeader>

          {!selectedProjectForDoc ? (
            <div className="space-y-0 max-h-80 overflow-y-auto divide-y divide-border border border-border">
              {projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => handleProjectSelected(project)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left"
                  data-testid={`project-picker-${project.id}`}
                >
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 border ${projectTypeColors[project.type]} uppercase tracking-wide shrink-0`}>
                    {project.type.replace('_', ' ')}
                  </span>
                  <p className="text-sm font-medium text-foreground truncate flex-1">
                    {project.title || project.subject || 'Untitled'}
                  </p>
                  <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-3 bg-muted border border-border text-sm">
                <p className="label-swiss mb-1">Attaching to</p>
                <p className="font-medium text-foreground">
                  {selectedProjectForDoc.title || selectedProjectForDoc.subject || selectedProjectForDoc.type}
                </p>
              </div>

              <GoogleDocsPicker
                projectId={selectedProjectForDoc.id}
                onDocSelected={handleDocSelected}
                buttonText="Select from Google Drive"
                className="w-full"
              />

              <Button
                variant="ghost"
                className="w-full text-xs"
                onClick={() => setSelectedProjectForDoc(null)}
              >
                ← Choose different project
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCell = ({ label, value, className = '' }) => (
  <div className={`py-5 px-5 ${className}`}>
    <p className="text-3xl font-bold font-mono tracking-tight text-foreground">{value}</p>
    <p className="label-swiss mt-1">{label}</p>
  </div>
);

const SummaryCell = ({ label, value }) => (
  <div className="py-5 px-4 text-center">
    <p className="text-2xl font-bold font-mono tracking-tight text-foreground">{value}</p>
    <p className="label-swiss mt-1">{label}</p>
  </div>
);

export default DashboardPage;
